/**
 * Data transformation utilities for the Virtual Library API.
 * Converts backend API shapes to internal UI shapes.
 */
import { getFileType } from '../utils/fileUtils';
import bootConfig from '../utils/bootConfig';
import type {
  Category,
  ResourcesResponse,
  Role,
  ItemType,
  Audience,
} from 'types';
import type {
  NavCategory,
  NavResource,
  SearchResult,
  SearchCategoryMatch,
  RoleLocation,
  SearchType,
  SearchInField,
} from 'types/LibraryApi';

// ═══════════════════════════════════════════════════════════════════════════
// Public Transformations
// ═══════════════════════════════════════════════════════════════════════════

/** Transform flat NavCategory[] into Category[] for the category tree. */
export function transformNavCategories(navCats: NavCategory[]): Category[] {
  return navCats.map((nc) => ({
    id: nc.name,
    name: nc.name,
    hasChildren: nc.count > 0,
    count: nc.count,
    children: nc.count > 0 ? [] : undefined,
    // Preserve per-item permission flags from the API (used to gate edit/delete in manage mode)
    canModify: nc.canModify,
    canDelete: nc.canDelete,
  }));
}

/** Resolve the launch URL for a resource.
 */
export function resolveResourceUrl(rawUrl: string | undefined, id: number | string): string {
  const baseUrl = (bootConfig.libraryApiBaseUrl ?? '').replace(/\/api$/, '');
  return `${baseUrl}/library/launch.aspx?id=${id}`;
}

/** Transform NavResource[] into the paginated ResourcesResponse the UI expects. */
export function transformNavResources(navRes: NavResource[]): ResourcesResponse {
  return {
    items: navRes.map((nr) => ({
      id: String(nr.id),
      title: nr.name ?? '',
      description: nr.description ?? '',
      categoryId: '',
      type: determineResourceType(nr.url),
      audience: 'students' as Audience,
      tags: [],
      updatedAt: new Date().toISOString(),
      url: resolveResourceUrl(nr.url, nr.id),
      // Preserve per-item permission flags from the API (used to gate edit/delete in manage mode)
      canModify: nr.canModify,
      canDelete: nr.canDelete,
    })),
    total: navRes.length,
    page: 1,
    pageSize: navRes.length || 10,
  };
}

/** Transform SearchResult[] into ResourcesResponse (resource rows only). */
export function transformSearchResults(results: SearchResult[]): ResourcesResponse {
  const resourceItems = results.filter((sr) => sr.idLibraryResource !== null);
  return {
    items: resourceItems.map((sr) => ({
      id: String(sr.idLibraryResource),
      title: sr.libraryResourceName ?? '',
      description: sr.libraryResourceDescription ?? '',
      // Build a full breadcrumb path so the Folder column can disambiguate
      // resources whose immediate parent folder has the same name in different
      // locations (e.g. "State Testing" under two different My School paths).
      // lineage "Home/State Testing/" + cat "Guides and Resources"
      //   → "State Testing › Guides and Resources"
      // lineage "Home/" + cat "State Testing"
      //   → "State Testing"
      categoryId: buildResourceFolderBreadcrumb(sr.lineage, sr.libraryResourceCategoryName),
      // Type derivation mirrors the browse listing (transformNavResources),
      // which classifies from the url alone. The real /search payload currently
      // returns no `url`, so this degrades gracefully:
      //   • url present → exact type (http → 'guide', bare filename → extension);
      //                   identical to browse and to a future backend with `url`.
      //   • no url      → 'guide'; type is unknown without a url.
      type: sr.url ? determineResourceType(sr.url) : 'guide',
      audience: 'students',
      tags: [],
      updatedAt: new Date().toISOString(),
      url: resolveResourceUrl(sr.url ?? undefined, sr.idLibraryResource!),
      canModify: sr.canModifyResource,
      canDelete: sr.canDeleteResource,
    })),
    total: resourceItems.length,
    page: 1,
    pageSize: resourceItems.length || 10,
  };
}

/** Extract unique category-only matches (idLibraryResource === null) from search results.
 *
 * The real API can return the folder path in two shapes:
 *   Shape A — category name is explicit:
 *     lineage = '/Home/Science/'  libraryResourceCategoryName = 'Math'
 *     → fullPath = '/Home/Science/Math/'
 *
 *   Shape B — full path is already in lineage, name field is null:
 *     lineage = '/Home/Science/Math/'  libraryResourceCategoryName = null
 *     → extract name from the last segment of lineage
 */
export function transformSearchCategories(results: SearchResult[]): SearchCategoryMatch[] {
  const seen = new Set<string>();
  const matches: SearchCategoryMatch[] = [];
  for (const sr of results) {
    if (sr.idLibraryResource !== null) continue; // resource row — skip

    let name: string;
    let fullPath: string;

    if (sr.libraryResourceCategoryName) {
      // Shape A: lineage is the PARENT path, name is explicit
      const normalized = sr.lineage.startsWith('/') ? sr.lineage : '/' + sr.lineage;
      const parentPath = normalized.endsWith('/') ? normalized : normalized + '/';
      fullPath = parentPath + sr.libraryResourceCategoryName + '/';
      name = sr.libraryResourceCategoryName;
    } else {
      // Shape B: lineage IS the full path (name already embedded)
      const normalized = sr.lineage.startsWith('/') ? sr.lineage : '/' + sr.lineage;
      fullPath = normalized.endsWith('/') ? normalized : normalized + '/';
      const segments = fullPath.split('/').filter(Boolean);
      if (segments.length === 0) continue; // guard against malformed lineage
      name = segments[segments.length - 1];
    }

    if (!seen.has(fullPath)) {
      seen.add(fullPath);
      matches.push({ name, fullPath });
    }
  }
  return matches;
}

/** Transform RoleLocation[] into Role[]. */
export function transformRoleLocations(roleLocs: RoleLocation[]): Role[] {
  return roleLocs.map((rl) => ({ id: rl.id, name: rl.name }));
}

// ═══════════════════════════════════════════════════════════════════════════
// Search Parameter Mapping
// ═══════════════════════════════════════════════════════════════════════════

export function mapSearchMode(mode?: string): SearchType {
  switch (mode) {
    case 'any':   return 'anyOfTheseWords';
    case 'exact': return 'exactPhrase';
    default:      return 'allOfTheseWords';
  }
}

export function mapSearchInFields(fields?: Array<'category' | 'name' | 'description'>): SearchInField[] {
  if (!fields || fields.length === 0) return [];
  return fields.map((f) => {
    switch (f) {
      case 'category':    return 'categoryName';
      case 'name':        return 'resourceName';
      case 'description': return 'resourceDescription';
    }
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Private Helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Determine resource type from URL - distinguishes between external links and file resources.
 *  External URLs (http/https) are treated as 'guide'.
 *  File paths/names are analyzed for their extension and mapped to appropriate file type.
 */
function determineResourceType(url: string | undefined): ItemType {
  if (!url) return 'guide';
  
  // If it's a full URL (external link), treat as guide
  if (/^https?:\/\//i.test(url)) return 'guide';
  
  // Otherwise, detect file type from extension
  return getFileType(url);
}

/**
 * Builds a human-readable folder breadcrumb for a resource search result.
 * Strips the "Home/" prefix and appends the immediate category name so
 * resources in different locations are visually distinguishable.
 *
 * Examples:
 *   lineage "Home/",                    cat "State Testing"
 *     → "State Testing"
 *   lineage "Home/State Testing/",      cat "Guides and Resources"
 *     → "State Testing › Guides and Resources"
 *   lineage "Home/My School: FCA/State Testing/", cat "State Testing Tribune"
 *     → "My School: FCA › State Testing › State Testing Tribune"
 */
function buildResourceFolderBreadcrumb(lineage: string, categoryName: string | null): string {
  if (!categoryName) return '';
  const ancestors = lineage.split('/').filter(Boolean).slice(1); // strip 'Home'
  return [...ancestors, categoryName].join(' › ');
}
